/**
 * 从查询字符串中提取 token 参数的值
 * @param {string} searchStr - 不含 '?' 前缀的查询字符串
 * @returns {string|null} 解码后的 token 值，未找到则返回 null
 */
const getTokenValue = searchStr => {
  if (!searchStr) return null
  const segments = searchStr.split('&')
  for (const seg of segments) {
    const eqIdx = seg.indexOf('=')
    const key = eqIdx === -1 ? seg : seg.substring(0, eqIdx)
    if (key === 'token') {
      const raw = eqIdx === -1 ? '' : seg.substring(eqIdx + 1)
      let decoded = ''
      try {
        decoded = decodeURIComponent(raw.replace(/\+/g, ' '))
      } catch (error) {
        decoded = raw
      }
      return decoded || raw
    }
  }
  return null
}

/**
 * 从查询字符串中移除 token 参数，保留其余参数原始字节不变
 * @param {string} searchStr - 不含 '?' 前缀的查询字符串
 * @returns {{ changed: boolean, result: string }} changed 表示是否发生了移除
 */
const removeTokenParam = searchStr => {
  if (!searchStr) {
    return { changed: false, result: searchStr }
  }
  const segments = searchStr.split('&')
  const filtered = segments.filter(seg => {
    const eqIdx = seg.indexOf('=')
    const key = eqIdx === -1 ? seg : seg.substring(0, eqIdx)
    return key !== 'token'
  })
  if (filtered.length === segments.length) {
    return { changed: false, result: searchStr }
  }
  return { changed: true, result: filtered.join('&') }
}

/**
 * 从当前页面 URL 中提取 token，存入 sessionStorage 后从 URL 中移除。
 */
export const extractAndCleanToken = () => {
  const href = window.location.href

  const hashIndex = href.indexOf('#')
  const baseAndQuery = hashIndex !== -1 ? href.substring(0, hashIndex) : href
  const hashPart = hashIndex !== -1 ? href.substring(hashIndex + 1) : ''

  const mainQIndex = baseAndQuery.indexOf('?')
  const mainSearchStr =
    mainQIndex !== -1 ? baseAndQuery.substring(mainQIndex + 1) : ''
  const mainToken = getTokenValue(mainSearchStr)

  const hashQIndex = hashPart.indexOf('?')
  const hashSearchStr =
    hashQIndex !== -1 ? hashPart.substring(hashQIndex + 1) : ''
  const hashToken = getTokenValue(hashSearchStr)

  const token = mainToken || hashToken
  if (!token) return

  sessionStorage.setItem('auth_token', token)

  let newBaseAndQuery = baseAndQuery
  if (mainSearchStr) {
    const { changed, result } = removeTokenParam(mainSearchStr)
    if (changed) {
      const base = baseAndQuery.substring(0, mainQIndex)
      newBaseAndQuery = result ? `${base}?${result}` : base
    }
  }

  let newHashPart = hashPart
  if (hashSearchStr) {
    const { changed, result } = removeTokenParam(hashSearchStr)
    if (changed) {
      const hashPath = hashPart.substring(0, hashQIndex)
      newHashPart = result ? `${hashPath}?${result}` : hashPath
    }
  }

  const newHref =
    hashIndex !== -1 ? `${newBaseAndQuery}#${newHashPart}` : newBaseAndQuery

  if (newHref !== href) {
    window.history.replaceState({}, '', newHref)
  }
}

/**
 * 获取当前会话存储的 token
 * @returns {string} token 值，未存储时返回空字符串
 */
export const getToken = () => {
  return sessionStorage.getItem('auth_token') || ''
}

export const normalizeLoginInfo = loginInfo => {
  const token = getToken()
  if (!token) return loginInfo || {}
  return {
    ...(loginInfo || {}),
    token,
  }
}

/**
 * 将 token 回写到当前 URL 的主查询参数中。
 */
export const restoreTokenToUrl = () => {
  const token = getToken()
  if (!token) return
  const href = window.location.href
  const hashIndex = href.indexOf('#')
  const beforeHash = hashIndex !== -1 ? href.substring(0, hashIndex) : href
  const hash = hashIndex !== -1 ? href.substring(hashIndex) : ''

  if (/(^|[?&])token=/.test(beforeHash)) return

  const separator = beforeHash.includes('?') ? '&' : '?'
  const newHref = `${beforeHash}${separator}token=${encodeURIComponent(
    token
  )}${hash}`
  window.history.replaceState({}, '', newHref)
}

let pageShowInited = false

export const initTokenGuard = pageShow => {
  extractAndCleanToken()
  if (pageShowInited || !pageShow?.on) return
  pageShow.on(extractAndCleanToken)
  pageShowInited = true
}
